import { FormSectionHandler } from '../entity.handler';
import { IoTypeResponse, IoTypeUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, EnumToFormBuilderComboValues, NumberField, MemoField } from '../utils';
import { ICONS } from '../icons';

import * as Papa from 'papaparse'; // csv parser

export const IO_TYPES = ['digital_input', 'digital_output', 'analog_input', 'temperature_input', 'can_input', 'counter_input', 'camera_input', 'value_input'];

interface EditorValues {
    name?: string;
    type?: string;
    active?: string;
    inactive?: string;
    smoothingType?: string;
    unit?: string;
    rateUnit?: string;
    rateMultiplier?: number;
    rateType?: string;
    lookups?: string;
}

export class IoTypeDetailsSectionHandler implements FormSectionHandler<IoTypeResponse, EditorValues> {

    type = 'form';
    icon = ICONS.iotype;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.IOTYPE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: IoTypeResponse, payload: IoTypeUpdateRequest): Promise<IoTypeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateIoType(entity.id, payload));
    }

    lookupsToCsv(lookups: IdName[]): string {
        const arr = lookups.map(item => {
            return [item.id, item.name];
        });
        return Papa.unparse(arr, {
            header: false,
            skipEmptyLines: true,
        });
    }

    csvToLookups(csv: string): IdName[] {
        const result = Papa.parse(csv, {
            header: false,
        });

        if (result.errors && result.errors.length > 0) {
            throw new Error(`${result.errors[0].message} (line ${result.errors[0].row + 1})`);
        }
        return result.data.map(line => ({ id: line[0], name: line[1] }));
    }

    async mapEntityToEditor(entity: IoTypeResponse): Promise<EditorValues> {

        const result = {
            name: entity.name,
            type: entity.type,
            active: entity.text && entity.text.active,
            inactive: entity.text && entity.text.inactive,
            smoothingType: entity.smoothingType,
            unit: entity.unit,
            rateUnit: entity.rate && entity.rate.unit,
            rateMultiplier: entity.rate && entity.rate.multiplier,
            rateType: entity.rate && entity.rate.type,
            lookups: entity.lookups && this.lookupsToCsv(entity.lookups),
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    async mapEditorToEntity(entity: IoTypeUpdateRequest, values: EditorValues): Promise<IoTypeUpdateRequest> {

        const patch: IoTypeUpdateRequest = {
            name: values.name,
            type: values.type as any,
            text: values.active || values.inactive ? { active: values.active, inactive: values.inactive } : undefined,
            smoothingType: values.smoothingType as any,
            unit: values.unit,
            rate: values.rateMultiplier !== undefined || values.rateUnit !== undefined || values.rateType ? { multiplier: values.rateMultiplier, unit: values.rateUnit, type: values.rateType as any } : undefined,
            lookups: values.lookups && this.csvToLookups(values.lookups),
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object

        return Promise.resolve(EntityUtils.mergeFields(['name', 'type', 'text', 'smoothingType', 'unit', 'rate', 'lookups'], patch, entity));

    }

    getFields(entity: IoTypeResponse): Promise<FormBuilderField[]> {

        const activeText = TextField('active', this.translate('FIELDS.ACTIVE_TEXT'), false, 1, 255);
        const inactiveText = TextField('inactive', this.translate('FIELDS.INACTIVE_TEXT'), false, 1, 255);
        const unit = TextField('unit', this.translate('FIELDS.UNIT'), false, 1, 32);
        const smoothingType = ComboField('smoothingType', this.translate('FIELDS.FILTER'), true, EnumToFormBuilderComboValues(this.admin.i18n, 'ADMIN.EDITORS.IOTYPE.SMOOTING_TYPES', ['none', 'moving_average_5', 'moving_average_10', 'moving_average_20']));

        const rateType = ComboField('rateType', this.translate('FIELDS.RATE_TYPE'), true, EnumToFormBuilderComboValues(this.admin.i18n, 'ADMIN.EDITORS.IOTYPE.RATE_TYPES', ['none', 'val_per_km', 'km_per_val', 'val_per_mile', 'mile_per_val', 'val_per_hour', 'hour_per_val']));
        const rateUnit = TextField('rateUnit', this.translate('FIELDS.RATE_UNIT'), false, 1, 32);
        const rateMultipler = NumberField('rateMultiplier', this.translate('FIELDS.RATE_MULTIPLIER'), true, 0, 255e10, null);

        const lookups = { ...MemoField('lookups', this.translate('FIELDS.LOOKUPS'), false, 0, 255e10), description: this.translate('FIELDS.LOOKUPS_DESC'), options: { size: '10' } };

        const typeValues: FormBuilderKeyValue[] = [
            { key: 'digital_input', value: '', fields: [activeText, inactiveText] },
            { key: 'digital_output', value: '', fields: [activeText, inactiveText] },
            { key: 'analog_input', value: '', fields: [unit, smoothingType] },
            { key: 'temperature_input', value: '', fields: [smoothingType] },
            { key: 'can_input', value: '', fields: [activeText, inactiveText, unit, smoothingType] },
            { key: 'counter_input', value: '', fields: [unit, rateType, rateMultipler, rateUnit] },
            { key: 'camera_input', value: '', fields: [] },
            { key: 'value_input', value: '', fields: [lookups] },
        ];
        typeValues.forEach(field => {
            field.value = this.admin.i18n.instant(`ADMIN.EDITORS.IOTYPE.TYPES.${field.key.toUpperCase()}`);
        });

        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                { ...ComboField('type', this.translate('FIELDS.TYPE'), true, typeValues), readonly: !this.creating },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

}
